import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Play } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine, Overline, SectionHeading } from "../components/Reveal";
import { AbstractVisual, ParticleField } from "../components/AbstractVisual";
import { Marquee } from "../components/Marquee";
import { ExperienceCard } from "../components/ExperienceCard";
import { PartnersSection } from "../components/PartnersSection";
import { api } from "../lib/api";

const PILLARS = [
  { n: "01", title: "CREATE", sub: "Original XR Experiences", to: "/studio" },
  { n: "02", title: "DISTRIBUTE", sub: "Global XR Content", to: "/distribution" },
  { n: "03", title: "OPERATE", sub: "Immersive Venues", to: "/locations" },
  { n: "04", title: "PLAY", sub: "VR Games & Attractions", to: "/games" },
];

const BUSINESS = [
  { title: "XR Production", headline: "We Create New Worlds", desc: "From concept and storytelling to technology and deployment, we collaborate with creators, filmmakers, institutions and brands to develop unforgettable immersive experiences.", cta: "Explore Production", to: "/studio" },
  { title: "Global Distribution", headline: "World-Class XR. New Markets.", desc: "We partner with international XR studios and content creators to introduce extraordinary immersive experiences to audiences across India and global markets.", cta: "Become a Distribution Partner", to: "/distribution" },
  { title: "Immersive Experiences", headline: "Step Inside the Story", desc: "Large-scale free-roaming VR and XR experiences designed for museums, malls, cultural attractions, entertainment venues and dedicated immersive destinations.", cta: "Discover Experiences", to: "/experiences" },
  { title: "VR Games", headline: "Adventure Without Limits", desc: "High-energy VR attractions ranging from bungee jumping and paragliding to cricket, racing and multiplayer entertainment.", cta: "Explore VR Games", to: "/games" },
];

const INDUSTRIES = ["Entertainment", "Museums & Culture", "Tourism", "Education", "Real Estate", "Corporate", "Sports", "Retail", "Events", "Theme Parks"];

const INDIA_CITIES = ["Delhi NCR", "Mumbai", "Hyderabad", "Bengaluru", "Chennai", "Pune", "Ahmedabad", "Kolkata"];

const WORKFLOW = ["Concept", "Research", "Storytelling", "Experience Design", "3D / Virtual Production", "XR Development", "Testing", "Deployment", "Distribution"];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 260]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <AbstractVisual hue={222} seed={7} />
      <ParticleField hue={222} />
      <div className="absolute bottom-0 inset-x-0 h-[45vh] hero-grid-floor" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#030305] via-transparent to-[#030305]/60" />
      <motion.div style={{ y, opacity }} className="relative max-w-[1440px] mx-auto px-6 md:px-10 pb-28 md:pb-36 pt-48 w-full">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-400 mb-8">
          VR / XR Entertainment · Production · Global Distribution
        </motion.p>
        <h1 className="font-serif tracking-tight leading-[0.98] text-[13vw] md:text-[7.5rem] lg:text-[9rem]" data-testid="hero-headline">
          <MaskedLine delay={0.35}>Immersive Worlds.</MaskedLine>
          <MaskedLine delay={0.5}><span className="italic text-zinc-400">Limitless Possibilities.</span></MaskedLine>
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-8 max-w-xl text-base md:text-lg text-zinc-400 font-light leading-relaxed">
          We create, distribute and operate world-class VR and XR experiences — connecting global immersive creators with audiences across India and beyond.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/experiences" data-testid="hero-cta-explore"
            className="px-8 py-3.5 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors duration-300 inline-flex items-center gap-2">
            Explore Experiences <ArrowRight size={15} />
          </Link>
          <Link to="/creators" data-testid="hero-cta-partner"
            className="px-8 py-3.5 rounded-full border border-white/20 text-sm hover:bg-white/10 transition-colors duration-300">
            Partner With Us
          </Link>
          <Link to="/distribution" data-testid="hero-cta-india"
            className="text-sm text-zinc-400 hover:text-white transition-colors duration-300 inline-flex items-center gap-1.5 group">
            Bring Your XR Experience to India <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </motion.div>
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
        <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-600">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent" />
      </motion.div>
    </section>
  );
};

export default function Home() {
  const [experiences, setExperiences] = useState([]);
  const [games, setGames] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    api.list("experiences", { featured: true }).then(setExperiences).catch(() => {});
    api.list("games", { featured: true }).then(setGames).catch(() => {});
    api.list("news").then((d) => setNews(d.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <main>
      <Seo path="/" />
      <Hero />
      <Marquee />

      {/* Vision */}
      <section data-testid="vision-section" className="relative py-28 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline className="mb-5">The Vision</Overline>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif tracking-tight leading-[1.05]">
                Beyond Reality.<br /><span className="italic text-zinc-400">Beyond Entertainment.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-end">
            <Reveal delay={0.15}>
              <p className="text-base md:text-lg text-zinc-300 font-light leading-relaxed">
                Vrova works at the intersection of <span className="text-white">technology, storytelling, entertainment and experience</span>. We create extraordinary immersive experiences, distribute the world's best XR content and make next-generation entertainment accessible to millions of people. From original productions to global licensing, from flagship venues to pop-up worlds — we are building the infrastructure of immersive entertainment.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section data-testid="pillars-section" className="border-t border-white/[0.06]">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
          {PILLARS.map((p, i) => (
            <Link key={p.n} to={p.to} data-testid={`pillar-${p.title.toLowerCase()}`}
              className="group relative border-b md:border-b-0 xl:border-r border-white/[0.06] p-10 md:p-14 overflow-hidden hover:bg-white/[0.02] transition-colors duration-500">
              <Reveal delay={i * 0.08}>
                <p className="font-serif text-7xl md:text-8xl text-white/[0.06] leading-none select-none group-hover:text-white/[0.12] transition-colors duration-500">{p.n}</p>
                <h3 className="font-serif text-3xl md:text-4xl mt-8 mb-3 tracking-tight">{p.title}</h3>
                <p className="text-sm text-zinc-500 font-light">{p.sub}</p>
                <ArrowUpRight size={18} className="mt-8 text-zinc-600 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500" />
              </Reveal>
            </Link>
          ))}
        </div>
      </section>

      {/* Our Business */}
      <section data-testid="business-section" className="py-28 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <SectionHeading overline="What We Do" title={<>One Company.<br /><span className="italic text-zinc-400">Four Ways Into New Worlds.</span></>} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16">
            {BUSINESS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08}>
                <Link to={b.to} data-testid={`business-card-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group block relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-10 md:p-14 h-full hover:border-white/25 hover:-translate-y-1.5 transition-all duration-500 poster-sheen">
                  <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-6">{b.title}</p>
                  <h3 className="font-serif text-3xl md:text-4xl tracking-tight mb-5">{b.headline}</h3>
                  <p className="text-zinc-400 font-light leading-relaxed mb-10 max-w-md">{b.desc}</p>
                  <span className="inline-flex items-center gap-2 text-sm text-zinc-300 group-hover:text-white transition-colors">
                    {b.cta} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <PartnersSection />

      {/* Featured Experiences */}
      <section data-testid="featured-experiences-section" className="py-28 md:py-40 border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHeading overline="Featured Immersive Experiences" title={<>Stories You <span className="italic text-zinc-400">Step Inside.</span></>} />
            <Reveal delay={0.2}>
              <Link to="/experiences" data-testid="view-all-experiences" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors pb-2">
                All Experiences <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="max-w-2xl mt-6">
            <p className="text-zinc-400 font-light leading-relaxed" data-testid="featured-experiences-subheading">
              The worlds we're exploring for the future of immersive entertainment.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {experiences.map((x, i) => (
              <Reveal key={x.id} delay={i * 0.08}><ExperienceCard item={x} type="experiences" index={i} hideStatus /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VR Games */}
      <section data-testid="games-section" className="py-28 md:py-40 border-t border-white/[0.06] relative overflow-hidden">
        <AbstractVisual hue={14} seed={21} className="opacity-30" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHeading overline="VR Games & Attractions" title={<>Adventure <span className="italic text-zinc-400">Without Limits.</span></>} />
            <Reveal delay={0.2}>
              <Link to="/games" data-testid="view-all-games" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors pb-2">
                All VR Games <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
          <div className="flex gap-6 mt-16 overflow-x-auto pb-6 snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {games.map((g, i) => (
              <div key={g.id} className="snap-start shrink-0 w-[280px] md:w-[340px]">
                <ExperienceCard item={g} type="games" index={i} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Distribution band */}
      <section data-testid="distribution-band" className="relative py-32 md:py-48 border-t border-white/[0.06] overflow-hidden">
        <ParticleField hue={38} count={16} />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <Overline className="mb-6">Global XR Distribution</Overline>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05] max-w-4xl mx-auto">
              Have an immersive experience? <span className="italic text-zinc-400">We'll help you take it further.</span>
            </h2>
            <p className="mt-8 max-w-2xl mx-auto text-zinc-400 font-light leading-relaxed">
              Whether you're an XR studio looking to enter India, a venue searching for a unique attraction, or a brand ready to build a new immersive experience — let's create what comes next.
            </p>
            <Link to="/distribution" data-testid="distribution-band-cta"
              className="inline-flex items-center gap-2 mt-10 px-8 py-3.5 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors duration-300">
              Let's Build the Future <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* XR Studio preview */}
      <section data-testid="studio-preview" className="py-28 md:py-40 border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHeading overline="XR Studio" title={<>From an Idea to <span className="italic text-zinc-400">an Immersive World.</span></>} />
            <Reveal delay={0.2}>
              <Link to="/studio" data-testid="studio-preview-cta" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors pb-2">
                Inside the Studio <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
          <div className="flex gap-0 mt-16 overflow-x-auto snap-x [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden border-l border-white/10">
            {WORKFLOW.map((step, i) => (
              <Reveal key={step} delay={i * 0.05} className="shrink-0 snap-start">
                <div className="w-[220px] md:w-[260px] border-r border-white/10 px-8 py-10 group hover:bg-white/[0.02] transition-colors duration-500">
                  <p className="font-mono text-xs text-zinc-600 mb-6">{String(i + 1).padStart(2, "0")}</p>
                  <p className="font-serif text-xl md:text-2xl tracking-tight group-hover:translate-x-1 transition-transform duration-500">{step}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* B2B split */}
      <section data-testid="b2b-section" className="grid grid-cols-1 lg:grid-cols-2 border-t border-white/[0.06]">
        {[
          { tag: "For XR Creators", title: "Take Your XR Experience to New Markets", desc: "We partner with VR/XR studios, immersive storytellers and experience creators worldwide to distribute and operate exceptional immersive content across India and other emerging markets.", cta: "Become a Content Partner", to: "/creators", hue: 222, testid: "b2b-creators" },
          { tag: "For Venues & Investors", title: "Bring Immersive Entertainment to Your Venue", desc: "Malls, museums, resorts, theme parks and destinations — from revenue share to turnkey installations and permanent immersive venues.", cta: "Discuss a Project", to: "/venues", hue: 38, testid: "b2b-venues" },
        ].map((b) => (
          <Link key={b.tag} to={b.to} data-testid={b.testid}
            className="group relative overflow-hidden p-12 md:p-20 border-b lg:border-b-0 lg:border-r border-white/[0.06] min-h-[420px] flex flex-col justify-end poster-sheen">
            <AbstractVisual hue={b.hue} seed={b.hue} className="opacity-50" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#030305] via-[#030305]/50 to-transparent" />
            <div className="relative">
              <Overline className="mb-4">{b.tag}</Overline>
              <h3 className="font-serif text-3xl md:text-4xl tracking-tight mb-4 max-w-md">{b.title}</h3>
              <p className="text-zinc-400 font-light leading-relaxed max-w-md mb-8 text-sm md:text-base">{b.desc}</p>
              <span className="inline-flex items-center gap-2 text-sm text-white">
                {b.cta} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </div>
          </Link>
        ))}
      </section>

      {/* Industries */}
      <section data-testid="industries-section" className="py-28 md:py-40 border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <SectionHeading overline="Markets" title={<>XR For <span className="italic text-zinc-400">Every Industry.</span></>} />
          <div className="flex flex-wrap gap-3 mt-14">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind} delay={i * 0.04}>
                <Link to="/venues" data-testid={`industry-${ind.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="inline-block px-6 py-3.5 rounded-full border border-white/10 text-sm text-zinc-300 hover:text-white hover:border-white/35 hover:bg-white/[0.03] transition-all duration-400">
                  {ind}
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why India */}
      <section data-testid="why-india-section" className="relative py-28 md:py-40 border-t border-white/[0.06] overflow-hidden">
        <AbstractVisual hue={38} seed={42} className="opacity-35" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline className="mb-5">Why India</Overline>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                Your Gateway to One of the World's <span className="italic text-zinc-400">Largest Entertainment Markets.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-end">
            <Reveal delay={0.15}>
              <p className="text-zinc-300 font-light leading-relaxed mb-10">
                A massive young population, rapid mall and entertainment infrastructure growth, a strong technology ecosystem and rising demand for premium experiences — India is ready for immersive entertainment at scale.
              </p>
              <div className="flex flex-wrap gap-2.5 mb-10">
                {INDIA_CITIES.map((c) => (
                  <span key={c} className="px-4 py-2 rounded-full border border-white/10 text-xs font-mono text-zinc-400">{c}</span>
                ))}
              </div>
              <Link to="/distribution" data-testid="expand-into-india-cta" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-white/20 text-sm hover:bg-white/10 transition-colors duration-300">
                Expand Into India <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Locations preview */}
      <section data-testid="locations-preview" className="py-28 md:py-36 border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHeading overline="Locations" title={<>Where Worlds <span className="italic text-zinc-400">Open Their Doors.</span></>} />
            <Reveal delay={0.2}>
              <Link to="/locations" data-testid="view-locations" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors pb-2">
                All Locations <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-14">
            {["Hyderabad", "Mumbai", "Bengaluru", "Delhi NCR", "Chennai", "Pune"].map((city, i) => (
              <Reveal key={city} delay={i * 0.05}>
                <Link to="/locations" data-testid={`location-preview-${city.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="block rounded-xl border border-white/10 p-6 hover:border-white/30 hover:bg-white/[0.02] transition-all duration-400 group">
                  <p className="font-serif text-lg md:text-xl group-hover:translate-x-0.5 transition-transform duration-300">{city}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Latest News */}
      <section data-testid="news-section" className="py-28 md:py-40 border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHeading overline="Insights" title={<>Latest From <span className="italic text-zinc-400">the Frontier.</span></>} />
            <Reveal delay={0.2}>
              <Link to="/insights" data-testid="view-all-insights" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors pb-2">
                All Insights <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
            {news.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.08}>
                <Link to={`/insights/${a.slug}`} data-testid={`news-card-${a.slug}`}
                  className="group block rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden hover:border-white/25 hover:-translate-y-1.5 transition-all duration-500 h-full">
                  <div className="relative h-44"><AbstractVisual hue={a.hue ?? 222} seed={i + 3} /></div>
                  <div className="p-7">
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3">{a.category} · {a.date}</p>
                    <h3 className="font-serif text-xl leading-snug group-hover:text-white text-zinc-100 transition-colors">{a.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="final-cta" className="relative py-36 md:py-56 border-t border-white/[0.06] overflow-hidden">
        <AbstractVisual hue={260} seed={99} className="opacity-45" />
        <ParticleField hue={260} count={18} />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <Overline className="mb-6">Global Partnerships</Overline>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-8xl tracking-tight leading-[1.02] max-w-5xl mx-auto">
              Let's Build <span className="italic text-zinc-400">the Future.</span>
            </h2>
            <div className="mt-12 flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-testid="final-cta-contact"
                className="px-9 py-4 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors duration-300 inline-flex items-center gap-2">
                Start a Conversation <ArrowRight size={15} />
              </Link>
              <Link to="/venues" data-testid="final-cta-venues"
                className="px-9 py-4 rounded-full border border-white/20 text-sm hover:bg-white/10 transition-colors duration-300">
                Discuss a Venue Project
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
